import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/date';
import { sprintPace } from '../lib/sprint-pace';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SprintItem } from '@hygge/shared';
import { Calendar, Layers, Target, Timer, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';

interface SprintInfoBarProps {
  sprints: SprintItem[];
  selectedSprintId: string | null;
  onSprintChange: (sprintId: string | null) => void;
  className?: string;
}

export function SprintInfoBar({ sprints, selectedSprintId, onSprintChange, className }: SprintInfoBarProps) {
  const activeSprints = useMemo(() => sprints.filter((s) => s.status === 'active'), [sprints]);
  const completedSprints = useMemo(
    () => sprints.filter((s) => s.status === 'completed').sort(
      (a, b) => new Date(b.completedAt ?? b.updatedAt).getTime() - new Date(a.completedAt ?? a.updatedAt).getTime(),
    ),
    [sprints],
  );

  const selectedSprint = sprints.find((s) => s.id === selectedSprintId);

  const selectValue = selectedSprintId ?? 'all';

  const handleChange = (value: string) => {
    onSprintChange(value === 'all' ? null : value);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-4 px-6 py-2 border-b border-border bg-muted/20 text-xs',
        className,
      )}
    >
      {/* Sprint selector */}
      <Select value={selectValue} onValueChange={handleChange}>
        <SelectTrigger className="w-auto h-7 gap-1.5 border-none bg-transparent hover:bg-accent/50 pl-1.5 pr-2 text-xs font-semibold text-foreground shadow-none focus:ring-0">
          <div className="flex items-center gap-1.5">
            {selectedSprint?.status === 'active' ? (
              <Timer className="h-3.5 w-3.5 text-green-500" />
            ) : selectedSprint?.status === 'completed' ? (
              <Timer className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
              All tasks
            </div>
          </SelectItem>

          {activeSprints.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Active</SelectLabel>
              {activeSprints.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                    {s.name}
                    <span className="text-muted-foreground ml-1">
                      {s.taskCount} task{s.taskCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          )}

          {completedSprints.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Completed</SelectLabel>
              {completedSprints.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                    {s.name}
                    <span className="text-muted-foreground ml-1">
                      {s.taskCount} task{s.taskCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>

      {/* Sprint details (only when a sprint is selected) */}
      {selectedSprint && <SprintDetails sprint={selectedSprint} />}
    </div>
  );
}

/**
 * The bar used to do its own sprint arithmetic — its own window length, its own
 * elapsed share, its own points percentage — beside `sprint-pace.ts`, which
 * does the same sums for the Boards page and now for the board Overview. The
 * two disagreed: this one measured the window with `daysBetweenDateOnly` and so
 * called a Monday-to-Friday sprint four days long, while the shared rule counts
 * both ends and calls it five. Two rules that can disagree about the same sprint
 * is one rule too many, so this reads the shared one.
 */
function SprintDetails({ sprint }: { sprint: SprintItem }) {
  const pace = useMemo(() => sprintPace(sprint), [sprint]);

  // `daysLeft` is negative once the end date has passed, which is what lets the
  // label say "overdue" out loud instead of clamping to a reassuring "0d left".
  const overdueBy = pace.daysLeft !== null && pace.daysLeft < 0 ? -pace.daysLeft : 0;
  const daysRemaining = Math.max(0, pace.daysLeft ?? 0);
  const pointsPct = Math.round(pace.donePct ?? 0);

  const endDateLabel = sprint.endDate
    ? formatDate(sprint.endDate, 'monthDay')
    : null;

  const startDateLabel = sprint.startDate
    ? formatDate(sprint.startDate, 'monthDay')
    : null;

  return (
    <>
      {/* Goal */}
      {sprint.goal && (
        <>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-1 text-muted-foreground max-w-[200px] truncate">
            <Target className="h-3 w-3 shrink-0" />
            <span className="truncate">{sprint.goal}</span>
          </div>
        </>
      )}

      <div className="h-4 w-px bg-border" />

      {/* Points progress with bar */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <TrendingUp className="h-3 w-3" />
        <span className="tabular-nums">
          {sprint.completedPoints}/{sprint.totalPoints} pts
        </span>
        <div className="w-16 h-1.5 rounded-full bg-border overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              pointsPct === 100 ? 'bg-green-500' : pointsPct > 0 ? 'bg-hygge' : 'bg-border',
            )}
            style={{ width: `${pointsPct}%` }}
          />
        </div>
        <span className="text-muted-foreground/60 tabular-nums">{pointsPct}%</span>
      </div>

      {/* Date info */}
      {sprint.status === 'active' && pace.daysLeft !== null && (
        <>
          <div className="h-4 w-px bg-border" />
          <div
            className={cn(
              'flex items-center gap-1.5',
              overdueBy > 0 ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            <Calendar className="h-3 w-3" />
            {overdueBy > 0 ? (
              <span className="font-medium tabular-nums">{overdueBy}d overdue</span>
            ) : (
              <span className="tabular-nums">{daysRemaining}d left</span>
            )}
            {endDateLabel && (
              <span className={overdueBy > 0 ? 'text-destructive/70' : 'text-muted-foreground/60'}>
                ({overdueBy > 0 ? 'ended' : 'ends'} {endDateLabel})
              </span>
            )}
          </div>
        </>
      )}

      {/* Completed sprint date range */}
      {sprint.status === 'completed' && startDateLabel && endDateLabel && (
        <>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{startDateLabel} — {endDateLabel}</span>
          </div>
          {sprint.velocity !== null && (
            <>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <span>Velocity: <span className="text-foreground font-medium tabular-nums">{sprint.velocity}</span></span>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
